/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: conversion-template-dropdown-component.ts
 *
 * Created by kyang on 2020-08-03.
 */

function ConversionTemplateDropdownController (
    this: JS5Object,
    _: JS5Object,
    $animate: JS5Object,
    $attrs: JS5Object,
    $element: JS5Object,
    $scope: JS5Object,
    ModalService: JS5Object,
    Rx: JS5Object,
) {
    this.conversionTypeObservable = new Rx.BehaviorSubject(null);
    this.conversionTemplateObservable = new Rx.BehaviorSubject(null);


    //===================//
    // Template Response //
    //===================//

    this.isTypeRequired = () => {
        return this.forceTypeRequired || this.isRequired;
    }

    this.isTypePromptVisible = () => {
        return _.size(this.typeWhitelist) !== 1 || this.conversionTypes.length !== 1;
    };

    this.isTemplatePromptVisible = () => {
        return this.selectedType() !== 'Passthrough';
    };

    this.isTemplatePromptEnabled = () => {
        return !!this.selectedType() || !this.isTypePromptVisible();
    };

    this.getTemplatePrompt = () => {
        let prefix = _.get(this, 'templatePromptPrefix', '');
        let selectedType = this.selectedType() || '';
        let suffix = _.get(this, 'templatePromptSuffix', '');
        return `${prefix} ${selectedType} ${suffix}`.trim();
    };

    this.selectedType = (newType?: string) => {
        if (newType === undefined) {
            return this.conversionTypeObservable.value;
        } else {
            this.conversionTypeObservable.onNext(newType);
        }
    };

    const updateTemplateChoices = () => {
        this.conversionTemplates = _.filter(this.collection, {converterType: this.selectedType()});
    };

    const onTypeChanged = () => {
        let selectedType = this.selectedType();
        let selectedTemplate = this.selectedTemplate();
        updateTemplateChoices();
        _.invoke(this, 'onTypeChange', {type: selectedType});
        if (selectedTemplate && selectedTemplate.converterType !== selectedType) {
            this.selectedTemplate(null);
        }
    };

    this.selectedTemplate = (newTemplate?: JS5Object) => {
        if (newTemplate === undefined) {
            return this.conversionTemplateObservable.value;
        } else {
            this.conversionTemplateObservable.onNext(newTemplate);
        }
    };

    const onTemplateChanged = () => {
        let newTemplateName = _.get(this.selectedTemplate(), 'name');
        if (this.ngModelCtrl.$modelValue !== newTemplateName) {
            this.ngModelCtrl.$setViewValue(newTemplateName);
        }
        _.invoke(this, 'onTemplateChange', {template: this.selectedTemplate()});
    };

    this.templateHasDescription = () => {
        return !!_.get(this.selectedTemplate(), 'description');
    };

    this.openDescriptionModal = () => {
        return ModalService.showMessageModal({
            messageText: this.selectedTemplate().description,
            pullCancelLeft: true,
            title: `Template Description`,
            showOK: true,
            okText: 'Close',
            showCancel: false,
            windowClass: 'confirm-modal-sm',
        });
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    const updateConversionTypes = () => {
        if (_.isEmpty(this.typeWhitelist)) {
            this.conversionTypes = _.chain(this.collection)
                .keyBy('converterType')
                .keys()
                .sortBy()
                .value();
        } else {
            this.conversionTypes = _.chain(this.typeWhitelist)
                .values()
                .sortBy()
                .value();
            if (this.typeWhitelist.length == 1 && this.conversionTypes.length == 1) {
                this.defaultTemplateType = this.typeWhitelist[0];
            }
        }
        if (this.includePassthrough) {
            this.conversionTypes.push('Passthrough');
        }
    };

    const onTemplateNameChanged = (templateName: string) => {
        let newConversionTemplate = _.find(this.collection, {name: templateName});
        let selectedValidTemplate = newConversionTemplate && (_.isEmpty(this.typeWhitelist) || _.includes(this.typeWhitelist, newConversionTemplate.converterType));
        if ((!newConversionTemplate && templateName) || (newConversionTemplate && !selectedValidTemplate)) {
            this.selectedType(this.defaultTemplateType ? this.defaultTemplateType : null);
            this.selectedTemplate({name: templateName});
            return templateName;
        }
        if (newConversionTemplate) { // && selectedValidTemplate
            this.selectedTemplate(newConversionTemplate);
            this.selectedType(newConversionTemplate.converterType);
        } else { // templateName == null
            this.selectedTemplate(null);
            this.selectedType(this.defaultTemplateType ? this.defaultTemplateType : null);
        }
        return _.get(newConversionTemplate, 'name');
    }

    const onChildClassChanged = (element: JS5Object) => {
        if (!_.some(element.get(), (htmlElement: JS5Object) => htmlElement.localName.toLowerCase() === 'dropdown-select')) {
            return;
        }
        let nowPristine = !$element.find('dropdown-select').hasClass('ng-dirty');
        if (nowPristine && !this.innerControlsPristine) {
            $scope.$apply(() => {
                onTemplateNameChanged(this.ngModelCtrl.$viewValue);
            });
        }
        this.innerControlsPristine = nowPristine;
    };

    this.$onInit = () => {
        this.innerControlsPristine = true;
        this.conversionTemplates = [];
        this.destroyObservable = new Rx.Subject();
        this.ngModelCtrl.$formatters.push(onTemplateNameChanged);

        updateConversionTypes();
        if (this.defaultTemplateType) {
            this.selectedType(this.defaultTemplateType);
        }
        this.conversionTypeObservable
            .takeUntil(this.destroyObservable)
            .distinctUntilChanged()
            .subscribeOnNext(onTypeChanged);
        this.conversionTemplateObservable
            .takeUntil(this.destroyObservable)
            .distinctUntilChanged()
            .subscribeOnNext(onTemplateChanged);
    };

    this.$postLink = () => {
        this.promptWidthNum = parseInt(this.promptWidth);
        if (!_.isNumber(this.promptWidthNum) || this.promptWidthNum < 1 || Number.isNaN(this.promptWidthNum)) {
            this.promptWidthNum = 3;
        }
        $attrs.$observe('required', (nowRequired: boolean) => {
            this.isRequired = nowRequired;
        });
        $attrs.$observe('disabled', (nowDisabled: boolean) => {
            this.isDisabled = nowDisabled;
        });
        $animate.on('setClass', $element, onChildClassChanged);
    }

    this.$onChanges = (changes: JS5Object) => {
        if (_.has(changes, 'typeWhitelist')
            || _.has(changes, 'includePassthrough')
            || (_.isEmpty(this.typeWhitelist) && _.has(changes, 'collection'))) {
            updateConversionTypes();
        }
        if (_.has(changes, 'typeWhitelist') || _.has(changes, 'collection')) {
            onTemplateNameChanged(this.ngModelCtrl.$viewValue);
            updateTemplateChoices();
        }
    }

    this.$onDestroy = () => {
        this.destroyObservable.onCompleted();
    }
}

angular.module('prism-ui.components').component(
    'conversionTemplateDropdown',
    {
        bindings: {
            /*
             collection:          (array; required) List of conversion templates
                                  to pick from.
             defaultTemplateType: (string; optional) When template name is removed
                                  programmatically, this template type is selected.
                                  Default = null
             ngModel:             (any; required) Value == the name of the
                                  selected template. Handled by ngModelCtrl.
             */
            collection: '<',
            defaultTemplateType: '<?',

            /*
             Component attributes
             forceTypeRequired: (boolean; optional) If true, forces user to select
                             a template type. However, template is allowed to be null.
                             To require a template to be selected, use ngRequired.
                             If false, ngRequired determines if type must be selected.
             Note: ngRequired & ngDisabled processed via $attrs, not the bindings.
             */
            id: '@',
            ngRequired: '<',
            ngDisabled: '<',
            forceTypeRequired: '<?',

            /*
             Display attributes
             promptWidth:         (integer; optional) A number between 1-11 that
                                  determines which col-md-## class to use.
                                  Default = 3
             typeTooltipTemplate: (string; optional) Path to tooltip html to use
                                  for the type dropdown.
             typeTooltipClass:    (string; optional) Class to add onto the tooltip
                                  html specified above.
             */
            promptWidth: '@',
            typeTooltipTemplate: '<?',
            typeTooltipClass: '@?',

            /*
             Selection Responses
             typePrompt:        (string; required) String to display
                                next to type selection dropdown
             templatePromptPrefix: (string; optional) When a template type is selected,
                                   this is the first part of the prompt to display.
             templatePromptSuffix: (string; optional) When a template type is selected,
                                   this is the last part of the prompt to display.
             typeWhitelist: (string[]; optional) If specified, determines which types
                                can be selected.
                            If array only has one value && not including Passthrough,
                                the type prompt will not appear.
                                Type always == the single value. Also, forcibly sets
                                defaultTemplateType == the single value.
                            If not specified, user can select any type that has
                                templates in the system.
             includePassthrough: (boolean; optional) If specified, will add "Passthrough"
                                 as a template type. This type sets the template == null.
             */
            typePrompt: '@',
            templatePromptPrefix: '@',
            templatePromptSuffix: '@',
            typeWhitelist: '<',
            includePassthrough: '<',

            /*
             Events
             onTypeChange:     (function; optional) Is invoked every time
                               a different type is selected. E.g.
                               onTypeChange(newType)
             onTemplateChange: (function; optional) Is invoked every time
                               a different template is selected. E.g.
                               onTypeChange(newTemplate)
             ngChange:         (function; optional) Is invoked every time
                               a different template is selected.
                               Handled by ngModelCtrl.
             */
            // TODO: In Angular 2, replace function calls with Observables?
            onTypeChange: '&?',
            onTemplateChange: '&?',
        },
        require: {
            ngModelCtrl: '^ngModel',
        },
        controller: ConversionTemplateDropdownController,
        templateUrl: 'components/visual/conversionTemplateDropdown/conversion-template-dropdown-template.html'
    });
